import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Loader, AlertCircle, CheckCircle, Phone, Eye, EyeOff } from 'lucide-react';
import StickyNavHeader from '../components/header/StickyNavHeader';
import Footer from '../components/footer/Footer';
import { useAuth } from '../hooks/useAuth.js';
import { fetchUserProfile } from '../domain/useCases/User/fetchUserProfile.js';
import { fetchProfile } from '../domain/useCases/User/fetchProfile.js';
import { fetchCurrentUser } from '../domain/useCases/User/fetchCurrentUser.js';
import { requestOtp } from '../domain/useCases/Auth/requestOtp.js';
import { verifyForgotOtp } from '../domain/useCases/Auth/verifyForgotOtp.js';
import { updatePassword } from '../domain/useCases/Auth/updatePassword.js';
import { resetPassword } from '../domain/useCases/Auth/resetPassword.js';
import { storageService } from '../domain/services/StorageService.js';

import {
  PhoneStepForm, PasswordLoginStepForm, ForgotPasswordPhoneStepForm,
  ForgotPasswordOtpStepForm, SetNewPasswordStepForm, OtpStepForm,
  SetPasswordAfterOtpStepForm, RegisterStepForm
} from '../components/auth/AuthForms.jsx';
const Login = () => {
  const navigate = useNavigate();
  const { sendOtp, verifyOtp, loginWithPassword, registerUser, logout: authLogout, loading: authLoading, error: authError } = useAuth();
  const [loginMethod, setLoginMethod] = useState('otp');
  const [step, setStep] = useState('phone');
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [error, setError] = useState(authError);
  const [success, setSuccess] = useState(null);
  const [resendTimer, setResendTimer] = useState(0);
  const [showPassword, setShowPassword] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  /**
   * Format phone number for API
   */
  const formatPhoneNumber = (phoneNumber) => {
    // Remove all non-digit characters
    const cleaned = phoneNumber.replace(/\D/g, '');

    // Return the cleaned number as is (10 digits for Nepali numbers)
    return cleaned;
  };

  /**
   * Login with password
   */
  const handlePasswordLogin = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (!phone.trim()) {
      setError('Please enter your phone number');
      return;
    }

    if (!password.trim()) {
      setError('Please enter your password');
      return;
    }

    try {
      const authData = await loginWithPassword(phone, password);

      // Fetch user data
      const userData = await fetchCurrentUser();
      storageService.setUserData(userData);

      setSuccess('Login successful! Redirecting...');

      setTimeout(() => {
        navigate('/dashboard');
      }, 1500);
    } catch (err) {
      setError(err.message);
    }
  };

  /**
   * Request OTP for forgot password
   */
  const handleForgotPasswordOtp = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (!phone.trim()) {
      setError('Please enter your phone number');
      return;
    }

    setLoading(true);

    try {
      await sendOtp(phone);

      setSuccess('OTP sent successfully! Check your phone.');
      setStep('forgot-password-otp');
      setResendTimer(60);

      const timer = setInterval(() => {
        setResendTimer(prev => {
          if (prev <= 1) {
            clearInterval(timer);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } catch (err) {
      console.error('Forgot password OTP error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Verify OTP for forgot password
   */
  const handleVerifyForgotPasswordOtp = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (!otp.trim()) {
      setError('Please enter the OTP');
      return;
    }

    if (otp.length !== 4) {
      setError('OTP must be 4 digits');
      return;
    }

    setLoading(true);

    try {
      await verifyForgotOtp(phone, otp);

      setSuccess('OTP verified! Please set your new password.');
      setStep('set-new-password');
      setOtp('');
    } catch (err) {
      console.error('Verify forgot password OTP error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Set password after OTP login (for new users without password)
   */
  const handleSetPasswordAfterOtp = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (!newPassword.trim()) {
      setError('Please enter your password');
      return;
    }

    if (!confirmPassword.trim()) {
      setError('Please confirm your password');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    try {
      await updatePassword(newPassword);

      setSuccess('Password set successfully! Redirecting to dashboard...');

      setTimeout(() => {
        navigate('/dashboard');
      }, 1500);
    } catch (err) {
      console.error('Set password after OTP error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Set new password after forgot password OTP verification
   */
  const handleSetNewPassword = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (!newPassword.trim()) {
      setError('Please enter your new password');
      return;
    }

    if (!confirmPassword.trim()) {
      setError('Please confirm your password');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    try {
      await resetPassword(phone, newPassword);

      setSuccess('Password reset successful! Redirecting to login...');

      setTimeout(() => {
        setStep('phone');
        setPhone('');
        setPassword('');
        setOtp('');
        setNewPassword('');
        setConfirmPassword('');
        setLoginMethod('otp');
      }, 2000);
    } catch (err) {
      console.error('Set password error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Request OTP
   */
  const handleRequestOtp = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (!phone.trim()) {
      setError('Please enter your phone number');
      return;
    }

    if (phone.replace(/\D/g, '').length !== 10) {
      setError('Please enter a valid 10-digit phone number');
      return;
    }

    setLoading(true);

    try {
      await sendOtp(phone);
      setSuccess('OTP sent successfully! Check your phone.');
      setStep('otp');
      setOtp(''); // Clear any previous OTP
      setResendTimer(60);

      // Start countdown timer
      const timer = setInterval(() => {
        setResendTimer(prev => {
          if (prev <= 1) {
            clearInterval(timer);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } catch (err) {
      console.error('Request OTP error:', err);
      
      if (err.response?.status === 404) {
        setError('Phone number not registered. Please register first.');
      } else if (err.response?.status === 429) {
        setError('Too many requests. Please wait before trying again.');
      } else {
        setError(err.message || 'Failed to send OTP. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  /**
   * Verify OTP and Login
   */
  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (!otp.trim()) {
      setError('Please enter the OTP');
      return;
    }

    if (otp.length !== 4) {
      setError('OTP must be 4 digits');
      return;
    }

    setLoading(true);

    try {
      const authData = await verifyOtp(phone, otp);

      // Fetch user data
      const userData = await fetchCurrentUser();
      storageService.setUserData(userData);

      // For registered users, proceed to dashboard
      setSuccess('Login successful! Redirecting...');

      // Redirect to dashboard after 1.5 seconds
      setTimeout(() => {
        navigate('/dashboard');
      }, 1500);
    } catch (err) {
      console.error('OTP verification error:', err);
      
      // Handle specific error cases
      if (err.response?.status === 401) {
        setError('Invalid OTP. Please try again.');
      } else if (err.response?.status === 404) {
        setError('Phone number not registered. Please register first.');
      } else if (err.response?.status === 429) {
        setError('Too many attempts. Please try again later.');
      } else if (err.message?.includes('expired')) {
        setError('OTP has expired. Please request a new one.');
      } else {
        setError(err.message || 'Failed to verify OTP. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  /**
   * Resend OTP
   */
  const handleResendOtp = async () => {
    if (resendTimer > 0) return;

    setError(null);
    setSuccess(null);
    setLoading(true);

    try {
      await sendOtp(phone);
      setSuccess('OTP resent successfully! Check your phone.');
      setResendTimer(60);
      setOtp(''); // Clear previous OTP

      // Start countdown timer
      const timer = setInterval(() => {
        setResendTimer(prev => {
          if (prev <= 1) {
            clearInterval(timer);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } catch (err) {
      console.error('Resend OTP error:', err);
      
      if (err.response?.status === 404) {
        setError('Phone number not registered.');
      } else if (err.response?.status === 429) {
        setError('Too many requests. Please wait before trying again.');
      } else {
        setError(err.message || 'Failed to resend OTP. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  /**
   * Register new user
   */
  const handleRegister = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (!name.trim()) {
      setError('Please enter your name');
      return;
    }

    if (!email.trim()) {
      setError('Please enter your email');
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('Please enter a valid email');
      return;
    }

    try {
      await registerUser(phone, name, email);
      setSuccess('Registration successful! Logging you in...');

      // Now login with the registered account
      setTimeout(() => {
        handleLoginAfterRegister();
      }, 1500);
    } catch (err) {
      setError(err.message);
    }
  };

  /**
   * Login after registration
   */
  const handleLoginAfterRegister = async () => {
    setError(null);

    try {
      const authData = await verifyOtp(phone, otp);

      // Fetch user data
      const userData = await fetchCurrentUser();
      storageService.setUserData(userData);

      setSuccess('Welcome! Redirecting to your dashboard...');
      setTimeout(() => {
        navigate('/dashboard');
      }, 1000);
    } catch (err) {
      setError(err.message);
    }
  };


  const vm = {
    step, setStep,
    loginMethod, setLoginMethod,
    phone, setPhone,
    otp, setOtp,
    password, setPassword,
    confirmPassword, setConfirmPassword,
    showPassword, setShowPassword,
    showConfirmPassword, setShowConfirmPassword,
    name, setName,
    email, setEmail,
    loading, setLoading,
    error, setError,
    success, setSuccess,
    resendTimer, setResendTimer,
    showNewPassword, setShowNewPassword,

    handlePasswordLogin,
    handleForgotPasswordOtp,
    handleVerifyForgotPasswordOtp,
    handleSetPasswordAfterOtp,
    handleSetNewPassword,
    handleRequestOtp,
    handleVerifyOtp,
    handleResendOtp,
    handleRegister,
    handleLoginAfterRegister
  };
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black">
      <StickyNavHeader />

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Login Container */}
        <div className="max-w-md mx-auto">
          <div className="bg-gradient-to-br from-white/10 to-white/5 rounded-2xl shadow-2xl p-8 backdrop-blur-xl border border-white/20">
            {/* Header */}
            <div className="text-center mb-8">
              <h1 className="text-4xl font-bold bg-gradient-to-r from-white via-green-200 to-orange-300 bg-clip-text text-transparent mb-2">
                {step === 'register' ? 'Complete Registration' : 'Login'}
              </h1>
              <p className="text-gray-300 mb-6">
                {step === 'phone' || step === 'password' ? (loginMethod === 'otp' ? 'Enter your phone number' : 'Enter your credentials') : step === 'otp' ? 'Enter the OTP sent to your phone' : 'Complete your profile'}
              </p>

              {/* Login Method Toggle */}
              {(step === 'phone' || step === 'password') && (
                <div className="flex gap-2 bg-white/10 rounded-lg p-1 border border-white/20 mb-4">
                  <button
                    type="button"
                    onClick={() => {
                      setLoginMethod('otp');
                      setStep('phone');
                      setPassword('');
                      setError(null);
                      setSuccess(null);
                    }}
                    className={`flex-1 py-2 px-4 rounded-md font-semibold transition-all ${loginMethod === 'otp'
                      ? 'bg-green-500 text-white'
                      : 'text-gray-300 hover:text-white'
                      }`}
                  >
                    OTP Login
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setLoginMethod('password');
                      setStep('password');
                      setOtp('');
                      setError(null);
                      setSuccess(null);
                    }}
                    className={`flex-1 py-2 px-4 rounded-md font-semibold transition-all ${loginMethod === 'password'
                      ? 'bg-green-500 text-white'
                      : 'text-gray-300 hover:text-white'
                      }`}
                  >
                    Password Login
                  </button>
                </div>
              )}
            </div>

            {/* Alert Messages */}
            {error && (
              <div className="mb-6 bg-red-50/10 border border-red-500/30 rounded-lg p-4 flex items-center gap-3">
                <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
                <p className="text-red-300 text-sm">{error}</p>
              </div>
            )}

            {success && (
              <div className="mb-6 bg-green-50/10 border border-green-500/30 rounded-lg p-4 flex items-center gap-3">
                <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
                <p className="text-green-300 text-sm">{success}</p>
              </div>
            )}

            
            {step === 'phone' && <PhoneStepForm vm={vm} />}
            {step === 'password' && <PasswordLoginStepForm vm={vm} />}
            {step === 'forgot-password-phone' && <ForgotPasswordPhoneStepForm vm={vm} />}
            {step === 'forgot-password-otp' && <ForgotPasswordOtpStepForm vm={vm} />}
            {step === 'set-new-password' && <SetNewPasswordStepForm vm={vm} />}
            {step === 'otp' && <OtpStepForm vm={vm} />}
            {step === 'set-password-after-otp' && <SetPasswordAfterOtpStepForm vm={vm} />}
            {step === 'register' && <RegisterStepForm vm={vm} />}

            {/* Footer */}
            <div className="mt-8 pt-6 border-t border-white/10 text-center">
              <p className="text-gray-400 text-sm">
                Don't have an account? Contact support to register.
              </p>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Login;
